// src/runtime/CRMRuntime/types.ts
// ============================================
// CRM Runtime 类型定义（Sprint 4 Phase A-1）
//
// 客户档案全部存在 Obsidian Vault 的 CRM/*.md 中
// 每个客户一个 Markdown 文件，包含 frontmatter + 正文章节
// ============================================

// ============================================
// 客户状态与优先级
// ============================================

/** 客户跟进状态 */
export type CustomerStatus =
  | 'prospecting'    // 潜在客户，初次接触
  | 'negotiating'    // 谈判中，快成交
  | 'closed-won'     // 已成交
  | 'closed-lost'    // 流失
  | 'active';        // 活跃客户（已成交且持续合作）

/** 客户优先级 */
export type CustomerPriority = 'high' | 'medium' | 'low';

/** 价值等级（S+ > S > A > B > C） */
export type CustomerTier = 'S+' | 'S' | 'A' | 'B' | 'C';

/**
 * 客户业务分类
 * - hotel: 酒店客户（酒店集团/连锁酒店/单体酒店，含自助机与SaaS）
 * - ai: AI 类客户（AI/SaaS/大模型/智能硬件/科技企业）
 * - potential: 潜在客户（初次接触、尚未明确需求）
 * - tracking: 跟踪中客户（已建联、正在跟进、谈判中）
 * - active: 已成交客户（已签合同/已付款/正在实施或售后）
 * - other: 其他（未归类）
 */
export type CustomerCategory = 'hotel' | 'ai' | 'potential' | 'tracking' | 'active' | 'other';

/** 客户分类显示配置 */
export const CUSTOMER_CATEGORY_CONFIG: Record<
  CustomerCategory,
  { label: string; color: string; description: string }
> = {
  hotel: { label: '酒店', color: '#FBBF24', description: '酒店集团/连锁/单体酒店' },
  ai: { label: 'AI', color: '#38BDF8', description: 'AI/SaaS/大模型/智能硬件' },
  potential: { label: '潜在', color: '#A78BFA', description: '初次接触，尚未明确需求' },
  tracking: { label: '跟踪', color: '#FB923C', description: '已建联，正在跟进谈判' },
  active: { label: '已成交', color: '#34D399', description: '已签合同/已付款/实施中' },
  other: { label: '其他', color: '#9CA3AF', description: '未归类客户' },
};

// ============================================
// 客户档案模型
// ============================================

/** 联系人信息 */
export interface CustomerContact {
  name: string;
  phone: string;
  wechat: string;
  email: string;
}

/** 跟进信息 */
export interface CustomerFollowup {
  /** 最后联系日期 YYYY-MM-DD */
  lastContact: string;
  /** 下次联系日期 YYYY-MM-DD */
  nextContact: string;
  status: CustomerStatus;
  priority: CustomerPriority;
  /** 预估成交金额（元） */
  estimatedValue: number;
}

/** 客户档案（完整模型，对应一个 .md 文件） */
export interface CustomerProfile {
  /** 客户 ID（文件名去掉 .md） */
  id: string;
  /** 公司名称 */
  company: string;
  /** 行业 */
  industry: string;
  /** 地区 */
  region: string;
  /** 标签 */
  tags: string[];
  /** 价值等级 */
  tier: CustomerTier;
  /** 业务分类（酒店/AI/潜在/跟踪/已成交/其他） */
  category: CustomerCategory;
  /** 线索来源（P0#4 归因：公众号/抖音/知乎/GEO基线/官网/转介绍/其他） */
  source: string;
  /** 负责人 */
  owner: string;
  /** 联系人信息 */
  contact: CustomerContact;
  /** 跟进信息 */
  followup: CustomerFollowup;
  /** 历史沟通（正文 ## 历史沟通 章节） */
  history: string;
  /** 成交记录（正文 ## 成交记录 章节） */
  deals: string;
  /** AI 分析（正文 ## AI 分析 章节） */
  analysis: string;
  /** 下一步建议（正文 ## 下一步建议 章节） */
  suggestion: string;
  /** 文件绝对路径（浏览器环境下为虚拟路径） */
  filePath: string;
}

// ============================================
// 查询与命令枚举
// ============================================

export enum CRMQuery {
  /** 列出所有客户 */
  List = 'list',
  /** 获取单个客户 */
  Get = 'get',
  /** 搜索客户 */
  Search = 'search',
  /** 找出 N 天未联系的客户 */
  FindStale = 'find_stale',
  /** 找出快成交客户 */
  FindClosing = 'find_closing',
  /** 找出应发案例的客户 */
  FindNeedingCase = 'find_needing_case',
  /** 统计概览 */
  Stats = 'stats',
  /** Phase2-2.3：今日回款集合（成交→记账→回款闭环，含客户归因） */
  TodayCollections = 'today_collections',
}

/** Phase2-2.3：单笔今日回款（来源于 CRM Payment 完成态） */
export interface TodayCollection {
  /** 关联成交单 ID（Payment.id，即记账凭证号） */
  dealId: string;
  customerId: string;
  customerName: string; // 客户/企业名（用于记账对手方）
  amount: number;
  /** 是否已回款确认（status=completed） */
  settled: boolean;
}

export enum CRMCommand {
  /** 创建客户档案 */
  Create = 'create',
  /** 更新客户档案 */
  Update = 'update',
  /** 追加沟通记录 */
  AppendHistory = 'append_history',
  /** 更新 AI 分析 */
  UpdateAnalysis = 'update_analysis',
  /** 更新下一步建议 */
  UpdateSuggestion = 'update_suggestion',
  /** 标记已联系（更新 lastContact） */
  MarkContacted = 'mark_contacted',
  /** Sprint 4 P0-2：CRM 自动成长（多渠道数据收集 + 客户画像自动构建） */
  AutoGrow = 'auto_grow',
  // Sprint 5：CRM Intelligence 命令
  /** 采集线索（从所有来源） */
  CollectLeads = 'collect_leads',
  /** 生成客户画像 */
  ProfileCustomer = 'profile_customer',
  /** 线索评分 */
  ScoreLead = 'score_lead',
  /** 产品推荐 */
  RecommendProduct = 'recommend_product',
  /** 生成跟进计划 */
  PlanFollowUp = 'plan_follow_up',
  /** 生成 CEO 简报 */
  GenerateCEOReport = 'generate_ceo_report',
  // Sprint 7 Phase 2：Business CRM Pipeline 命令
  /** 推进流水线阶段 */
  AdvanceStage = 'advance_stage',
  /** 获取客户完整流水线 */
  GetPipeline = 'get_pipeline',
  /** 获取某阶段统计 */
  GetStageMetrics = 'get_stage_metrics',
  /** 创建线索 */
  CreateLead = 'create_lead',
  /** 创建商机 */
  CreateOpportunity = 'create_opportunity',
  /** 创建报价 */
  CreateQuotation = 'create_quotation',
  /** 创建合同 */
  CreateContract = 'create_contract',
  /** 创建发票 */
  CreateInvoice = 'create_invoice',
  /** 创建付款 */
  CreatePayment = 'create_payment',
  /** 创建实施项目 */
  CreateProject = 'create_project',
  /** 创建售后服务工单 */
  CreateServiceTicket = 'create_service_ticket',
  /** 创建复购 */
  CreateRenewal = 'create_renewal',
  /** 创建裂变推荐 */
  CreateReferral = 'create_referral',
  /** 确保 Vault 中存在示例数据（幂等，按阶段补齐缺失数据） */
  SeedPipelineData = 'seed_pipeline_data',
}

/** CRM 统计概览 */
export interface CRMStats {
  total: number;
  byStatus: Record<CustomerStatus, number>;
  byPriority: Record<CustomerPriority, number>;
  byTier: Record<CustomerTier, number>;
  byCategory: Record<CustomerCategory, number>;
  totalEstimatedValue: number;
  staleCount: number;
  closingCount: number;
}

/** CRM Runtime 响应 */
export interface CRMResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

// ============================================
// Sprint 7 Phase 2：Business CRM 类型定义
//
// 将 CRM 从单纯的客户档案管理升级为完整的企业 CRM 流水线：
//   客户 → 需求 → 预算 → 产品 → 报价 → 合同 → 付款 → 实施 → 售后 → 复购 → 裂变
//
// 注意：本模块的 `Lead` 与 collector/types.ts 中的 `Lead`（采集线索）语义不同。
//   - collector Lead：从渠道采集的原始线索（含 source/message/raw）
//   - 本模块 Lead：Business CRM 流水线中的线索阶段实体（含 customerId/score/status 流转）
// 两者通过 customerId 关联，使用时按需从对应模块导入。
// ============================================

/** CRM 流水线阶段枚举 */
export enum CRMStage {
  /** 线索 */
  Lead = 'lead',
  /** 商机 */
  Opportunity = 'opportunity',
  /** 报价 */
  Quotation = 'quotation',
  /** 合同 */
  Contract = 'contract',
  /** 发票 */
  Invoice = 'invoice',
  /** 付款 */
  Payment = 'payment',
  /** 实施项目 */
  Project = 'project',
  /** 售后服务 */
  Service = 'service',
  /** 复购 */
  Renew = 'renew',
  /** 裂变推荐 */
  Referral = 'referral',
}

/** 报价明细项 */
export interface QuotationItem {
  /** 项目名称 */
  name: string;
  /** 数量 */
  quantity: number;
  /** 单价（元） */
  unitPrice: number;
  /** 金额（元）= quantity × unitPrice */
  amount: number;
}

/** 项目里程碑 */
export interface Milestone {
  /** 里程碑名称 */
  name: string;
  /** 截止日期 YYYY-MM-DD */
  dueDate: string;
  /** 状态 */
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
}

/** 线索（Business CRM 流水线阶段实体） */
export interface Lead {
  id: string;
  customerId: string;
  source: string;
  intent: string;
  budget: string;
  timeline: string;
  score: number;
  status: 'new' | 'qualified' | 'unqualified' | 'converted';
  createdAt: string;
  convertedToOpportunityId?: string;
}

/** 商机 */
export interface Opportunity {
  id: string;
  customerId: string;
  leadId?: string;
  title: string;
  description: string;
  budget: number;
  probability: number;
  expectedCloseDate: string;
  stage: CRMStage;
  status: 'open' | 'won' | 'lost';
  products: string[];
  competitors: string[];
  createdAt: string;
  updatedAt: string;
}

/** 报价 */
export interface Quotation {
  id: string;
  opportunityId: string;
  customerId: string;
  items: QuotationItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  validUntil: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  createdAt: string;
}

/** 合同 */
export interface Contract {
  id: string;
  quotationId: string;
  customerId: string;
  opportunityId: string;
  title: string;
  content: string;
  amount: number;
  signedDate: string;
  validUntil: string;
  status: 'draft' | 'sent' | 'signed' | 'active' | 'completed' | 'terminated';
}

/** 发票 */
export interface Invoice {
  id: string;
  contractId: string;
  customerId: string;
  amount: number;
  tax: number;
  total: number;
  issueDate: string;
  dueDate: string;
  status: 'issued' | 'paid' | 'overdue' | 'cancelled';
}

/** 付款 */
export interface Payment {
  id: string;
  invoiceId: string;
  customerId: string;
  amount: number;
  method: string;
  paidAt: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
}

/** 实施项目 */
export interface ImplementationProject {
  id: string;
  contractId: string;
  customerId: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  progress: number;
  milestones: Milestone[];
}

/** 售后服务工单 */
export interface ServiceTicket {
  id: string;
  customerId: string;
  projectId?: string;
  type: 'support' | 'bug' | 'feature_request' | 'training' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  createdAt: string;
  resolvedAt?: string;
}

/** 复购 */
export interface Renewal {
  id: string;
  customerId: string;
  originalContractId: string;
  newContractId?: string;
  type: 'renewal' | 'upgrade' | 'downgrade' | 'cross_sell';
  status: 'pending' | 'offered' | 'accepted' | 'rejected';
  estimatedValue: number;
  dueDate: string;
}

/** 裂变推荐 */
export interface Referral {
  id: string;
  referrerCustomerId: string;
  referredCustomerId: string;
  status: 'pending' | 'contacted' | 'converted' | 'lost';
  reward: string;
  convertedAt?: string;
}

/** 客户完整流水线摘要 */
export interface PipelineSummary {
  customerId: string;
  leads: Lead[];
  opportunities: Opportunity[];
  quotations: Quotation[];
  contracts: Contract[];
  invoices: Invoice[];
  payments: Payment[];
  projects: ImplementationProject[];
  serviceTickets: ServiceTicket[];
  renewals: Renewal[];
  referrals: Referral[];
  /** 当前所处最靠后阶段 */
  currentStage: CRMStage;
}

/** 单阶段统计 */
export interface StageMetrics {
  stage: CRMStage;
  total: number;
  /** 各状态计数 */
  byStatus: Record<string, number>;
  /** 阶段涉及的总金额（如商机预算/合同金额/付款金额合计） */
  totalAmount: number;
}

/** 阶段推进结果 */
export interface StageAdvanceResult {
  entityId: string;
  fromStage: CRMStage;
  toStage: CRMStage;
  /** 推进过程中创建的下游实体 ID（如 Lead → Opportunity 时新建的 oppId） */
  createdEntityId?: string;
  success: boolean;
  message?: string;
}
